import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import { rupiah } from "@/lib/format";
import { api } from "@/lib/pos-api";
import { Banknote, ArrowLeftRight, QrCode, SplitSquareHorizontal, Check, HandCoins, UserPlus, User, CreditCard, Plus, X } from "lucide-react";

const ICONS = { tunai: Banknote, transfer: ArrowLeftRight, qris: QrCode, kartu_debit: CreditCard, kartu_kredit: CreditCard };

// Rekomendasi uang tunai: pembulatan ke atas mendekati total (bukan pecahan tetap 10rb/20rb).
function cashSuggestions(T) {
  if (T <= 0) return [];
  const set = new Set();
  for (const step of [1000, 5000, 10000, 50000, 100000]) {
    const v = Math.ceil(T / step) * step;
    if (v > T) set.add(v);
  }
  return [...set].sort((a, b) => a - b).slice(0, 4);
}

export default function PaymentModal({ open, onClose, total, onConfirm, customer, onPickCustomer, keyboardMode = false }) {
  const [methods, setMethods] = useState([]);
  const [mode, setMode] = useState("tunai"); // method id | "split" | "piutang"
  const [cash, setCash] = useState("");
  const [dp, setDp] = useState("");
  const [lines, setLines] = useState([]);

  useEffect(() => {
    if (open) api.get("/payment-methods").then((r) => setMethods(r.data.filter((m) => m.active))).catch(() => setMethods([]));
  }, [open]);

  useEffect(() => {
    if (open) {
      setMode("tunai");
      setCash("");
      setDp("");
    }
  }, [open]);

  const byId = useMemo(() => Object.fromEntries(methods.map((m) => [m.id, m])), [methods]);
  const feePct = (id) => { const m = byId[id]; return m && !m.is_cash ? Number(m.fee_percent || 0) : 0; };

  // Init split lines when entering split mode
  useEffect(() => {
    if (mode === "split") {
      const cashM = methods.find((m) => m.is_cash) || methods[0];
      const otherM = methods.find((m) => !m.is_cash) || methods[0];
      if (cashM && otherM) setLines([{ method: cashM.id, amount: "" }, { method: otherM.id, amount: "" }]);
    }
  }, [mode]); // eslint-disable-line

  // ---- single method ----
  const singleFee = mode !== "split" && mode !== "piutang" ? Math.round(total * feePct(mode) / 100) : 0;
  const singleGrand = total + singleFee;
  const isCashMode = byId[mode]?.is_cash;
  const cashNum = Number(cash || 0);
  const change = isCashMode ? Math.max(0, cashNum - total) : 0;

  // ---- split ----
  const splitBases = lines.map((l) => Number(l.amount || 0));
  const sumExceptLast = splitBases.slice(0, -1).reduce((s, v) => s + v, 0);
  const lastBase = Math.max(0, total - sumExceptLast);
  const splitBasesFull = lines.map((l, i) => (i === lines.length - 1 ? lastBase : Number(l.amount || 0)));
  const splitFee = lines.reduce((s, l, i) => s + Math.round(splitBasesFull[i] * feePct(l.method) / 100), 0);
  const splitGrand = total + splitFee;
  const splitSumOk = Math.abs(splitBasesFull.reduce((s, v) => s + v, 0) - total) < 1 && sumExceptLast <= total;

  // ---- piutang ----
  const dpNum = Math.min(Number(dp || 0), total);
  const credit = Math.max(0, total - dpNum);

  let valid = false;
  if (mode === "split") valid = splitSumOk && lines.every((l) => l.method);
  else if (mode === "piutang") valid = !!customer;
  else if (isCashMode) valid = cashNum >= total;
  else valid = true;

  const confirm = () => {
    let payload;
    if (mode === "split") {
      payload = { payment_type: "split", payments: lines.map((l, i) => ({ method: l.method, amount: splitBasesFull[i] })), paid: splitGrand };
    } else if (mode === "piutang") {
      payload = { payment_type: "piutang", payments: dpNum > 0 ? [{ method: "tunai", amount: dpNum }] : [], paid: dpNum, customer_id: customer.id, customer_name: customer.name };
    } else if (isCashMode) {
      payload = { payment_type: mode, payments: [{ method: mode, amount: total }], paid: cashNum };
    } else {
      payload = { payment_type: mode, payments: [{ method: mode, amount: total }], paid: singleGrand };
    }
    if (customer && mode !== "piutang") { payload.customer_id = customer.id; payload.customer_name = customer.name; }
    onConfirm(payload);
  };

  const addLine = () => { if (lines.length < 4) { const m = methods.find((x) => !x.is_cash) || methods[0]; setLines((p) => [...p, { method: m?.id, amount: "" }]); } };
  const removeLine = (i) => setLines((p) => (p.length > 2 ? p.filter((_, x) => x !== i) : p));
  const setLine = (i, k, v) => setLines((p) => p.map((l, x) => (x === i ? { ...l, [k]: v } : l)));

  const tiles = (() => {
    const base = [
      ...methods.map((m) => ({ key: m.id, label: m.label, icon: ICONS[m.id] || CreditCard, fee: !m.is_cash && m.fee_percent > 0 ? m.fee_percent : 0 })),
      { key: "split", label: "Split", icon: SplitSquareHorizontal },
      { key: "piutang", label: "Piutang", icon: HandCoins },
    ];
    const used = new Set();
    return base.map((t) => {
      let sc = null;
      for (const ch of (t.label || "").toUpperCase()) {
        if (/[A-Z0-9]/.test(ch) && !used.has(ch)) { sc = ch; break; }
      }
      if (!sc) for (const d of "123456789") if (!used.has(d)) { sc = d; break; }
      if (sc) used.add(sc);
      return { ...t, sc };
    });
  })();

  const onKeyDown = (e) => {
    if (e.key === "Enter") { if (valid) { e.preventDefault(); confirm(); } return; }
    if (!keyboardMode) return;
    const k = (e.key || "").toUpperCase();
    const t = tiles.find((x) => x.sc === k);
    if (t) { e.preventDefault(); setMode(t.key); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="payment-modal" onKeyDown={onKeyDown} className="bg-ui-card border-ui-line text-ui-text max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Pembayaran</DialogTitle>
          <DialogDescription className="text-ui-muted text-xs">Pilih metode pembayaran dan selesaikan transaksi.</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl bg-ui-card border border-ui-line p-4 flex items-center justify-between">
          <span className="text-ui-muted">Total Tagihan</span>
          <span className="font-mono font-extrabold text-3xl text-brand-readable">{rupiah(total)}</span>
        </div>

        <button data-testid="payment-customer-btn" onClick={onPickCustomer} className="w-full flex items-center gap-2 rounded-xl border border-ui-line bg-ui-soft px-3 py-2.5 text-left hover:border-brand/60 no-select">
          {customer ? <User className="h-4 w-4 text-brand-readable" /> : <UserPlus className="h-4 w-4 text-ui-muted" />}
          <span className="text-sm flex-1 truncate">
            {customer ? <span className="text-ui-text font-semibold">{customer.name}</span> : <span className="text-ui-muted">Pilih pelanggan (F3) — wajib untuk Piutang</span>}
          </span>
          <span className="text-[11px] text-brand-readable">Ubah</span>
        </button>

        <div className="grid grid-cols-4 gap-2">
          {tiles.map((m) => (
            <button key={m.key} data-testid={`pay-method-${m.key}`} onClick={() => setMode(m.key)}
              className={`relative flex flex-col items-center gap-1 py-3 rounded-xl border transition-colors no-select ${mode === m.key ? "border-brand bg-brand-soft text-brand-readable" : "border-ui-line bg-ui-soft text-ui-muted hover:text-ui-text"}`}>
              <m.icon className="h-5 w-5" />
              <span className="text-[11px] font-semibold text-center leading-tight">{m.label}</span>
              {keyboardMode && m.sc ? <span className="absolute top-1 left-1 text-[9px] font-bold text-ui-muted bg-ui-soft/80 rounded px-1 border border-ui-line">{m.sc}</span> : null}
              {m.fee ? <span className="absolute top-1 right-1 text-[9px] font-bold text-warning-readable">+{m.fee}%</span> : null}
            </button>
          ))}
        </div>

        {/* Single cash */}
        {mode !== "split" && mode !== "piutang" && isCashMode && (
          <div className="space-y-3">
            <MoneyInput data-testid="cash-input" value={cash} onValueChange={setCash} placeholder="Uang diterima" className="h-14 text-right font-mono text-2xl bg-ui-card border-ui-line" autoFocus />
            <div className="grid grid-cols-4 gap-2">
              {cashSuggestions(total).map((q) => (
                <button key={q} data-testid={`quick-cash-${q}`} onClick={() => setCash(String(q))} className="h-11 rounded-lg bg-ui-card border border-ui-line text-sm font-mono font-semibold hover:border-brand no-select">
                  {rupiah(q).replace("Rp", "")}
                </button>
              ))}
              <button data-testid="quick-cash-pas" onClick={() => setCash(String(total))} className="h-11 rounded-lg bg-brand-soft border border-brand/50 text-brand-readable text-sm font-bold hover:bg-brand-soft no-select">Pas</button>
            </div>
            <div className="flex items-center justify-between px-1">
              <span className="text-ui-muted">Kembalian</span>
              <span data-testid="change-amount" className="font-mono font-bold text-xl text-warning-readable">{rupiah(change)}</span>
            </div>
          </div>
        )}

        {/* Single non-cash */}
        {mode !== "split" && mode !== "piutang" && !isCashMode && (
          <div className="space-y-2 py-2">
            {singleFee > 0 && (
              <>
                <div className="flex justify-between text-sm text-ui-muted"><span>Biaya {byId[mode]?.label} ({feePct(mode)}%)</span><span className="font-mono text-warning-readable">+{rupiah(singleFee)}</span></div>
                <div className="rounded-xl bg-ui-card border border-brand/40 p-3 flex items-center justify-between">
                  <span className="text-ui-muted font-semibold">Total Bayar</span>
                  <span data-testid="single-grand" className="font-mono font-bold text-2xl text-brand-readable">{rupiah(singleGrand)}</span>
                </div>
              </>
            )}
            <p className="text-ui-muted text-sm text-center">Konfirmasi setelah pembayaran {byId[mode]?.label} diterima.</p>
          </div>
        )}

        {/* Split */}
        {mode === "split" && (
          <div className="space-y-2">
            {lines.map((l, i) => {
              const isLast = i === lines.length - 1;
              const base = splitBasesFull[i];
              const f = Math.round(base * feePct(l.method) / 100);
              return (
                <div key={i} data-testid={`split-line-${i}`} className="flex items-center gap-2">
                  <select data-testid={`split-method-${i}`} value={l.method} onChange={(e) => setLine(i, "method", e.target.value)} className="h-11 rounded-lg bg-ui-card border border-ui-line text-sm px-2 w-36">
                    {methods.map((m) => <option key={m.id} value={m.id}>{m.label}{!m.is_cash && m.fee_percent > 0 ? ` +${m.fee_percent}%` : ""}</option>)}
                  </select>
                  {isLast ? (
                    <div className="flex-1 h-11 rounded-lg bg-ui-soft border border-ui-line flex items-center justify-end px-3 font-mono text-ui-muted" data-testid={`split-amount-${i}`}>{rupiah(base)}</div>
                  ) : (
                    <MoneyInput data-testid={`split-amount-${i}`} value={l.amount} onValueChange={(v) => setLine(i, "amount", v)} placeholder="0" className="flex-1 h-11 text-right font-mono bg-ui-card border-ui-line" />
                  )}
                  {f > 0 ? <span className="text-[10px] text-warning-readable w-16 text-right">+{rupiah(f)}</span> : <span className="w-16" />}
                  {!isLast && lines.length > 2 ? <button data-testid={`split-remove-${i}`} onClick={() => removeLine(i)} className="text-ui-muted hover:text-danger-readable"><X className="h-4 w-4" /></button> : <span className="w-4" />}
                </div>
              );
            })}
            {lines.length < 4 && <button data-testid="split-add-line" onClick={addLine} className="text-xs text-brand-readable flex items-center gap-1 hover:text-brand-readable"><Plus className="h-3.5 w-3.5" /> Tambah metode</button>}
            <div className="rounded-xl bg-ui-card border border-ui-line p-3 space-y-1">
              {splitFee > 0 && <div className="flex justify-between text-sm text-ui-muted"><span>Total Biaya</span><span className="font-mono text-warning-readable">+{rupiah(splitFee)}</span></div>}
              <div className="flex justify-between"><span className="text-ui-muted font-semibold">Total Bayar</span><span data-testid="split-grand" className="font-mono font-bold text-xl text-brand-readable">{rupiah(splitGrand)}</span></div>
            </div>
            {!splitSumOk && <p className="text-[11px] text-warning-readable text-center">Nominal metode melebihi total tagihan.</p>}
          </div>
        )}

        {/* Piutang */}
        {mode === "piutang" && (
          <div className="space-y-3">
            {!customer && <div data-testid="piutang-need-customer" className="text-xs text-warning-readable bg-warning-soft border border-warning/30 rounded-lg px-3 py-2">Pilih pelanggan dulu (F3) untuk mencatat piutang.</div>}
            <div>
              <label className="text-xs text-ui-muted uppercase tracking-wide">Uang Muka / DP (opsional)</label>
              <MoneyInput data-testid="piutang-dp-input" value={dp} onValueChange={setDp} placeholder="0" className="h-12 text-right font-mono text-lg bg-ui-card border-ui-line mt-1" />
            </div>
            <div className="rounded-xl bg-ui-card border border-danger/40 p-3 flex items-center justify-between">
              <span className="text-ui-muted flex items-center gap-2"><HandCoins className="h-4 w-4 text-danger-readable" /> Sisa Piutang</span>
              <span data-testid="piutang-credit-amount" className="font-mono font-bold text-lg text-danger-readable">{rupiah(credit)}</span>
            </div>
          </div>
        )}

        <Button data-testid="confirm-payment-btn" disabled={!valid} onClick={confirm} className="w-full h-14 text-lg font-bold bg-brand-button hover:bg-brand-button text-ui-text gap-2">
          <Check className="h-5 w-5" /> Selesaikan Pembayaran (Enter)
        </Button>
      </DialogContent>
    </Dialog>
  );
}
